import { useEffect, useState } from "react";
import { Save, Plus, X, Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";

const AdminSettings = () => {
  const { api, admin } = useAdminAuth();
  const isSuper = admin?.role === "super_admin";
  const [settings, setSettings] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)); }, [api]);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const save = async (fields) => {
    if (!isSuper) return toast.error("Super admin required");
    setBusy(true);
    try { const { data } = await api.patch("/admin/settings", fields); setSettings(data); toast.success("Settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="admin-settings-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Settings</span>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">Platform settings</h1>
          <p className="mt-1 text-sm text-[#0D1B2A]/60">These settings affect the entire platform.</p>
        </div>
        {!isSuper && <div className="inline-flex items-center gap-1.5 text-xs text-[#0D1B2A]/60 rounded-full px-3 py-1 bg-[#FFFBF5] border border-[#0D1B2A]/10"><Lock className="h-3.5 w-3.5" /> Read-only (support staff)</div>}
      </div>

      {/* Fee */}
      <section className="mt-8 rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid="settings-fee">
        <h2 className="font-display text-lg font-semibold">Platform fee</h2>
        <p className="text-sm text-[#0D1B2A]/60">Charged on successfully funded campaigns.</p>
        <div className="mt-4 flex items-end gap-3">
          <div>
            <label className="text-xs text-[#0D1B2A]/60">Percentage</label>
            <div className="relative">
              <Input type="number" step="0.5" min="0" max="30" disabled={!isSuper}
                value={settings.platform_fee_pct}
                onChange={(e) => update({ platform_fee_pct: parseFloat(e.target.value) || 0 })}
                data-testid="settings-fee-input"
                className="mt-1 h-11 w-40 rounded-xl border-[#0D1B2A]/15 pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D1B2A]/50">%</span>
            </div>
          </div>
          <button onClick={() => save({ platform_fee_pct: settings.platform_fee_pct })} disabled={busy || !isSuper}
            data-testid="settings-fee-save"
            className="rounded-xl bg-[#E85D04] hover:bg-[#c94d02] text-white h-11 px-4 text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2">
            <Save className="h-4 w-4" /> Save fee
          </button>
        </div>
      </section>

      {/* Categories */}
      <section className="mt-6 rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid="settings-categories">
        <h2 className="font-display text-lg font-semibold">Campaign categories</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {settings.categories.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-[#FFFBF5] border border-[#0D1B2A]/10 pl-3 pr-1 py-1 text-sm">
              <input value={c} onChange={(e) => {
                const list = [...settings.categories]; list[i] = e.target.value; update({ categories: list });
              }} className="bg-transparent border-0 outline-none w-32 text-[#0D1B2A]" disabled={!isSuper} />
              {isSuper && (
                <button onClick={() => update({ categories: settings.categories.filter((_, idx) => idx !== i) })}
                  className="h-6 w-6 grid place-items-center rounded-full hover:bg-red-100 text-[#0D1B2A]/60 hover:text-red-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
        {isSuper && (
          <div className="mt-4 flex items-center gap-2">
            <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Add new category"
              data-testid="settings-new-category-input"
              className="h-10 rounded-xl border-[#0D1B2A]/15 max-w-xs" />
            <button onClick={() => { if (newCategory.trim()) { update({ categories: [...settings.categories, newCategory.trim()] }); setNewCategory(""); } }}
              className="h-10 rounded-xl border border-[#0D1B2A]/15 hover:bg-[#0D1B2A]/5 px-3 text-sm inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Add</button>
            <button onClick={() => save({ categories: settings.categories })} disabled={busy}
              data-testid="settings-categories-save"
              className="ml-auto rounded-xl bg-[#E85D04] hover:bg-[#c94d02] text-white h-10 px-4 text-sm font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" /> Save categories</button>
          </div>
        )}
      </section>

      {/* Policies */}
      {[
        { key: "tos", title: "Terms of Service" },
        { key: "privacy", title: "Privacy Policy" },
        { key: "refund_policy", title: "Refund Policy" },
      ].map((f) => (
        <section key={f.key} className="mt-6 rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid={`settings-${f.key}`}>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">{f.title}</h2>
            {isSuper && (
              <button onClick={() => save({ [f.key]: settings[f.key] })} disabled={busy}
                data-testid={`settings-${f.key}-save`}
                className="rounded-xl bg-[#E85D04] hover:bg-[#c94d02] text-white h-9 px-3 text-xs font-semibold inline-flex items-center gap-1"><Save className="h-3.5 w-3.5" /> Save</button>
            )}
          </div>
          <Textarea rows={6} disabled={!isSuper} value={settings[f.key] || ""}
            onChange={(e) => update({ [f.key]: e.target.value })}
            className="mt-3 rounded-xl border-[#0D1B2A]/15 font-mono text-sm" />
          <p className="mt-2 text-xs text-[#0D1B2A]/50">Visible publicly at /{f.key === "tos" ? "terms" : f.key === "privacy" ? "privacy" : "refund"}.</p>
        </section>
      ))}
    </div>
  );
};

export default AdminSettings;
